/**
 * Storage access that never throws (OMG-2249).
 *
 * `localStorage` / `sessionStorage` throw synchronously (DOMException code 18,
 * "The operation is insecure" / "Access is denied for this document") when the
 * browser blocks storage: Safari with "Block All Cookies", Android WebViews with
 * partitioned storage, some in-app browsers. Before this helper, an unguarded
 * `localStorage.getItem` inside the outermost provider's effect
 * (createDataContext) reached React's root uncaught and unmounted the whole
 * app: a blank page for that cohort (Sentry NMH-REACT-V / -2BW, ~33 events per
 * user = a reload loop). The Sentry ignore patterns for those strings then hid
 * the first-party throw behind third-party noise.
 *
 * Contract: a blocked store reads as EMPTY and writes as NO-OPs. Callers keep
 * working with in-memory state for the page lifetime, which is the right
 * degradation for everything this app stores (locale, caption language,
 * remember-me hints, persisted UI state). Checkout has its own stricter
 * helpers (lib/checkout/idempotencyKey.ts) because it needs a stable in-memory
 * fallback VALUE, not just a safe read.
 *
 * Same convention as idempotencyKey.ts, BraintreeCheckout.tsx and
 * gaIdentifiers.ts, which already wrapped their own storage access.
 */

export interface SafeStorage {
  /** The stored value, or null when absent OR when storage is blocked. */
  getItem(key: string): string | null;
  /** true when the write landed, false when storage is blocked or full. */
  setItem(key: string, value: string): boolean;
  /** true when the removal ran, false when storage is blocked. */
  removeItem(key: string): boolean;
}

function wrap(getStore: () => Storage): SafeStorage {
  return {
    getItem(key) {
      try {
        return getStore().getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        getStore().setItem(key, value);
        return true;
      } catch {
        return false;
      }
    },
    removeItem(key) {
      try {
        getStore().removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  };
}

// The store is resolved per call, not captured at module load: merely touching
// `window.localStorage` throws in the blocked environments, so a module-level
// reference would move the crash to import time instead of removing it.
export const safeLocalStorage: SafeStorage = wrap(() => window.localStorage);
export const safeSessionStorage: SafeStorage = wrap(
  () => window.sessionStorage,
);

/**
 * Read a JSON value; null when absent, blocked, or not valid JSON. A corrupt
 * persisted blob must not take the app down any more than a blocked store.
 */
export function safeReadJson<T>(storage: SafeStorage, key: string): T | null {
  const raw = storage.getItem(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}
